import { productFiltersNames } from '@clubhub/constants/product-filters-names';
import {
  expatNotInUkFilterTitle,
  greenEcoFilterTitle,
  offsetFilterTitle,
  regulatedBTLFilterTitle,
  secondResidentialFilterTitle,
} from '@clubhub/ignite/constants/product-filters';
import { ProductsResultsColumn } from '@clubhub/ignite/models/products/product-results-configuration';
// Import every model from its own file, never the `@shared/models` barrel. Several files in that
// barrel import back into `@shared/constants`, so pulling the barrel in here creates an import
// cycle and this module evaluates before the enums exist (`RateType` reads as undefined).
import { LendingTypeCode } from '@shared/models/affordability-enums';
import { AffordabilityMet } from '@shared/models/enums/affordability-met.enum';
import { OutcomeResults } from '@shared/models/enums/outcome-results';
import { YesNoResults } from '@shared/models/enums/yes-no-results';
import { RateType } from '@shared/models/rate-type';

/**
 * Canonical, declarative descriptions of every filter that can be configured as a default
 * preference (distribution group level, and later broker level).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * A saved preference is matched back to its runtime filter definition by *display title*
 * (`ProductsFilterService.applySavedFilters` -> `setFilter` / `setIncludeExcludeFilter`, which
 * look the definition up by `title` and silently do nothing when no match is found). A title
 * that differs by so much as a plural therefore produces a preference that saves happily and
 * then never applies, with no error anywhere.
 *
 * So these titles are the single source of truth: the runtime filter definitions and the
 * preferences UI both read them from here, and `filter-preference-options.spec.ts` asserts every
 * title below still resolves against the real definitions for both lending types.
 *
 * Adding a filter option means adding it here, not in a component.
 */

export interface FilterPreferenceOption {
  /** Persisted value. For product filters this is also the display title - see above. */
  value: string;
  label: string;
}

export type SortDirectionKind = 'numeric' | 'alphabetical' | 'default';

export interface ProductSortPreferenceOption {
  /** Display title, and the value persisted for `Sort on`. */
  name: string;
  /** Absent for the `System default` entry, which maps to no stored sort. */
  column?: ProductsResultsColumn;
  direction: SortDirectionKind;
  lendingTypes: LendingTypeCode[];
}

/**
 * Shown wherever a filter has no explicit preference. One label everywhere - the tri-state grid, the
 * single selects, the multi-select summaries and the sort dropdowns all use this, so an admin never has
 * to work out whether "Use default" and "System default" mean different things.
 */
export const systemDefaultOptionLabel = 'System default';

// ---------------------------------------------------------------------------
// Product search - filter group titles
// ---------------------------------------------------------------------------

export const productFilterGroupTitles = {
  // These two already had constants; reuse rather than restate.
  rateType: productFiltersNames.rateType,
  initialProductPeriod: productFiltersNames.initialProductPeriod,
  // These four were raw literals in `products-filter.helper.ts` / `products-results.service.ts`.
  // This module is now their canonical home and those call sites import from here.
  productFeatures: 'Product features',
  tailoredProducts: 'Tailored products',
  criteria: 'Criteria',
  affordabilityMet: 'Affordability met',
} as const;

// ---------------------------------------------------------------------------
// Product search - Rate type
// ---------------------------------------------------------------------------

/**
 * Deliberately an allow-list, NOT `Object.values(RateType)`.
 *
 * `RateType` also contains `Lifetime`, which is excluded by product decision. Iterating the enum
 * would silently surface it, and would surface anything else added to the enum later.
 *
 * Note this is intentionally different from the runtime Rate type filter, whose options are
 * derived from the returned product set (`ProductsFilterHelper.resAndBtlProductFilterDefinitions`
 * -> `itemFactory`) so brokers only see rate types actually present in their results. An admin
 * configuring a default has no result set, so they get the full list. Do not "unify" these.
 *
 * `Tracker` needs no SONIA variant: the runtime `isMatchFuncFactory` already matches the key
 * `tracker` against both `Tracker` and `Tracker (SONIA)`.
 */
export const rateTypeFilterOptions: readonly FilterPreferenceOption[] = [
  RateType.Fixed,
  RateType.Discount,
  RateType.Variable,
  RateType.Tracker,
].map(rateType => ({ value: RateType[rateType], label: RateType[rateType] }));

// ---------------------------------------------------------------------------
// Product search - Initial product period
// ---------------------------------------------------------------------------

/** Mirrors `IgniteHelperService.initialPeriodRangeOptions`. */
export const initialPeriodRangeOptions: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 10, 15];

/** Mirrors `IgniteHelperService.forTermFilterName`. */
export const forTermFilterName = 'For term';

/** Mirrors `IgniteHelperService.customFilterName`. */
export const customInitialPeriodFilterName = 'Custom (months)';

export const initialProductPeriodFilterOptions: readonly FilterPreferenceOption[] = [
  ...initialPeriodRangeOptions.map(years => {
    const title = years === 1 ? '1 year' : `${years} years`;
    return { value: title, label: title };
  }),
  { value: forTermFilterName, label: forTermFilterName },
  { value: customInitialPeriodFilterName, label: customInitialPeriodFilterName },
];

// ---------------------------------------------------------------------------
// Product search - Product features
// ---------------------------------------------------------------------------

export const topSlicingFilterTitle = 'Allows top slicing';

const maxArrangementFeeFilterTitle = 'Max arrangement fee';

const sharedProductFeatureTitles = [
  'Cashback',
  'ERC\'s applied',
  'No ERC\'s applied',
  'Free legal',
  'Free valuation',
  maxArrangementFeeFilterTitle,
  'No arrangement fee',
  'No booking fee',
  'Overpayments allowed',
  'Portable',
  'Valuation fee',
] as const;

/**
 * Product features that carry a value as well as a tick, mirroring `numericFilterPrefix` /
 * `numericFilterDescription` on the runtime definitions.
 *
 * This is not cosmetic. The runtime `isMatchFunc` for Max arrangement fee returns **false** when the
 * value is missing, so a stored preference that selects it without a value would match no products at
 * all rather than simply ignoring the threshold. A selected numeric filter must always carry a value.
 */
export const productFeatureNumericInputs: Readonly<Record<string, { prefix: string; description: string }>> = {
  [maxArrangementFeeFilterTitle]: {
    prefix: '£',
    description: 'Enter the maximum arrangement fee to filter the products',
  },
};

export function hasNumericInput(filterName: string): boolean {
  return filterName in productFeatureNumericInputs;
}

/** Mirrors the runtime `numericFilterValidators`. Returns null when the value is acceptable. */
export function validateNumericFilterValue(value: number | null | undefined): string | null {
  if (value === null || value === undefined) {
    return 'Enter a value';
  }

  if (value < 1) {
    return 'Must be greater than 0';
  }

  return null;
}

/**
 * Validates one end of the Custom (months) range, mirroring the search form's own rules on these two fields:
 * required, a whole number, and not negative.
 *
 * Deliberately does not check that To is at or above From. The search form does not either, and a preference
 * that behaves differently from the search it seeds would be the worse inconsistency.
 */
export function validateCustomPeriodValue(value: number | null | undefined): string | null {
  if (value === null || value === undefined) {
    return 'Enter a value';
  }

  if (!Number.isInteger(value)) {
    return 'Must be a whole number';
  }

  if (value < 0) {
    return 'Must be 0 or more';
  }

  return null;
}

/**
 * Checks a stored product filter list for a selected numeric filter with no usable value. Structural
 * parameter type so this module does not depend on the API models.
 *
 * Worth blocking a save over: the runtime match function returns false when the value is missing, so such
 * a preference would silently match no products rather than being ignored.
 */
export function findInvalidNumericFilter(
  filters: readonly {
    filterName: string;
    isChecked: boolean;
    numericValue?: number | null;
    fromValue?: number | null;
    toValue?: number | null;
  }[],
): string | null {
  for (const filter of filters) {
    // Custom (months) carries a range rather than a single value, so it is checked on its own two fields.
    if (filter.isChecked && filter.filterName === customInitialPeriodFilterName) {
      const from = validateCustomPeriodValue(filter.fromValue);
      if (from) {
        return `${filter.filterName} from: ${from.toLowerCase()}`;
      }

      const to = validateCustomPeriodValue(filter.toValue);
      if (to) {
        return `${filter.filterName} to: ${to.toLowerCase()}`;
      }

      continue;
    }

    if (!filter.isChecked || !hasNumericInput(filter.filterName)) {
      continue;
    }

    const error = validateNumericFilterValue(filter.numericValue);
    if (error) {
      return `${filter.filterName}: ${error.toLowerCase()}`;
    }
  }

  return null;
}

export function productFeatureFilterOptions(lendingTypeCode: LendingTypeCode): FilterPreferenceOption[] {
  const titles = lendingTypeCode === LendingTypeCode.Btl
    ? [topSlicingFilterTitle, ...sharedProductFeatureTitles]
    : [...sharedProductFeatureTitles];

  return titles.map(title => ({ value: title, label: title }));
}

// ---------------------------------------------------------------------------
// Product search - Tailored products
// ---------------------------------------------------------------------------

export const tailoredProductTitles = {
  directOnly: 'Direct only',
  // Sourced from the existing constants, which the runtime definitions already use. Restating
  // these as literals is how 'Offset mortgage' became 'Offset mortgages' in an earlier draft.
  expatNotInUk: expatNotInUkFilterTitle,
  greenEco: greenEcoFilterTitle,
  offset: offsetFilterTitle,
  ownNew: 'Own new products',
  regulatedBtl: regulatedBTLFilterTitle,
  retrofit: 'Retrofit products',
  secondResidential: secondResidentialFilterTitle,
  packagers: 'Packagers',
} as const;

/**
 * Tailored products is not a checkbox list. Each item independently takes one of four states.
 *
 * Three of them mirror the runtime filter (`product-filter.component.ts` -> `setIncludeExcludeState`):
 * show only sets included, exclude sets excluded, and include in results sets neither. The fourth,
 * `system-default`, exists only here: it means the admin expressed no preference for that item.
 *
 * "Include in results" and "System default" produce the same *runtime* behaviour - `applySavedFilters`
 * removes a filter whose flags are both false - but they are different *preferences*. Only an explicit
 * choice can be locked against broker override, and only an explicit choice should survive a round trip
 * as a selected radio. So an item set to include in results is persisted as a row with both flags false,
 * and an item left at system default is not persisted at all.
 */
export type TailoredProductState = 'system-default' | 'show-only' | 'include' | 'exclude';

/** What an item is set to until the admin chooses otherwise. Never persisted. */
export const defaultTailoredProductState: TailoredProductState = 'system-default';

export const tailoredProductStateLabels: readonly { value: TailoredProductState; label: string }[] = [
  { value: 'system-default', label: systemDefaultOptionLabel },
  { value: 'show-only', label: 'Show only' },
  { value: 'include', label: 'Include in results' },
  { value: 'exclude', label: 'Exclude from results' },
];

export function tailoredProductFilterOptions(lendingTypeCode: LendingTypeCode): FilterPreferenceOption[] {
  const isBtl = lendingTypeCode === LendingTypeCode.Btl;

  const titles = [
    tailoredProductTitles.directOnly,
    tailoredProductTitles.expatNotInUk,
    tailoredProductTitles.greenEco,
    ...(isBtl ? [] : [tailoredProductTitles.offset]),
    ...(isBtl ? [] : [tailoredProductTitles.ownNew]),
    ...(isBtl ? [tailoredProductTitles.regulatedBtl] : []),
    tailoredProductTitles.retrofit,
    ...(isBtl ? [] : [tailoredProductTitles.secondResidential]),
    // Packagers carries nested sub-filters at runtime. The parent is configurable; its sub-filters
    // are out of scope, so a stored preference never populates `checkedSubFilters`.
    tailoredProductTitles.packagers,
  ];

  return titles.map(title => ({ value: title, label: title }));
}

// ---------------------------------------------------------------------------
// Product search - Criteria / Affordability met (grid column filters)
// ---------------------------------------------------------------------------

/**
 * These two are grid *column* filters on the product results table, not sidebar filters, and they
 * use different value domains from the identically named filters on the other two tools. Getting
 * these wrong is invisible until a saved preference fails to match a row.
 */
export const productCriteriaFilterOptions: readonly FilterPreferenceOption[] = [
  { value: OutcomeResults.Yes, label: 'Yes' },
  { value: OutcomeResults.No, label: 'No' },
  { value: OutcomeResults.Refer, label: 'Refer' },
];

/**
 * Matches `ProductsResultsService.getAffordabilityMetFilter` - Pass / Fail / empty, NOT `AffordabilityMet`.
 * Named constants rather than positions in the array below, so the grid column and the preferences screen
 * can share the same values without either depending on the other's ordering.
 */
export const affordabilityMetPassValue = 'Pass';
export const affordabilityMetFailValue = 'Fail';
export const affordabilityMetUnknownValue = '';

export const productAffordabilityMetFilterOptions: readonly FilterPreferenceOption[] = [
  { value: affordabilityMetPassValue, label: 'Yes' },
  { value: affordabilityMetFailValue, label: 'No' },
  { value: affordabilityMetUnknownValue, label: 'Unknown' },
];

// ---------------------------------------------------------------------------
// Affordability search
// ---------------------------------------------------------------------------

export const affordabilityFilterTitles = {
  affordabilityMet: 'Affordability met',
  criteriaOutcomes: 'Criteria outcomes',
  propertyOutcomes: 'Property outcomes',
  productMatched: 'Products matched',
} as const;

/** Mirrors the `affordabilityMatched` constant. */
export const affordabilityMetFilterOptions: readonly FilterPreferenceOption[] = [
  { value: AffordabilityMet.Yes, label: 'Yes' },
  { value: AffordabilityMet.No, label: 'No' },
  { value: AffordabilityMet.Unknown, label: 'No result' },
];

/** Mirrors the `outcomeList` constant, including the `Unset` / restricted entry. */
export const outcomeFilterOptions: readonly FilterPreferenceOption[] = [
  { value: OutcomeResults.Yes, label: 'Yes' },
  { value: OutcomeResults.No, label: 'No' },
  { value: OutcomeResults.Refer, label: 'Refer' },
  { value: OutcomeResults.Unset, label: 'Restricted' },
];

/** Mirrors the `productsMatched` constant. */
export const productMatchedFilterOptions: readonly FilterPreferenceOption[] = [
  { value: YesNoResults.Yes, label: 'Yes' },
  { value: YesNoResults.No, label: 'No' },
];

// ---------------------------------------------------------------------------
// Criteria search
// ---------------------------------------------------------------------------

export const meetsCriteriaFilterTitle = 'Meets criteria';

/**
 * The two sort rows on the Filter preferences table, named so a broker's "these are the slots I set" list can
 * refer to them the same way it refers to a filter group.
 *
 * Mirrored by `FilterPreferenceSortSlots` in the API. These are the row labels rendered in the template, so
 * changing one means changing all three.
 */
export const sortSlotTitles = {
  sortOn: 'Sort on',
  sortDirection: 'Sort direction',
} as const;

/** Mirrors the inline discrete filter on the criteria outcome grid - no `Unset` entry there. */
export const meetsCriteriaFilterOptions: readonly FilterPreferenceOption[] = [
  { value: OutcomeResults.Yes, label: 'Yes' },
  { value: OutcomeResults.No, label: 'No' },
  { value: OutcomeResults.Refer, label: 'Refer' },
];

// ---------------------------------------------------------------------------
// Product search - sort
// ---------------------------------------------------------------------------

const bothLendingTypes = [LendingTypeCode.Res, LendingTypeCode.Btl];
const btlOnly = [LendingTypeCode.Btl];

export const productSortPreferenceOptions: readonly ProductSortPreferenceOption[] = [
  { name: 'Max LTV', column: ProductsResultsColumn.MaxLtv, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Initial period (months)', column: ProductsResultsColumn.InitialPeriod, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Initial rate', column: ProductsResultsColumn.InitialRate, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Rate type', column: ProductsResultsColumn.RateType, direction: 'alphabetical', lendingTypes: bothLendingTypes },
  { name: 'Revert rate', column: ProductsResultsColumn.RevertRate, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Initial monthly payment', column: ProductsResultsColumn.InitialMonthlyPayment, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Monthly payment revert', column: ProductsResultsColumn.MonthlyPaymentRevert, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'True cost (initial period)', column: ProductsResultsColumn.TrueCostInitialPeriod, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'True cost (XX period)', column: ProductsResultsColumn.TrueCostCustomPeriod, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'True cost (term)', column: ProductsResultsColumn.TrueCostTerm, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Outstanding balance', column: ProductsResultsColumn.OutstandingBalance, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Cashback', column: ProductsResultsColumn.Cashback, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Total fees', column: ProductsResultsColumn.TotalFees, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Arrangement fee', column: ProductsResultsColumn.ArrangementFee, direction: 'numeric', lendingTypes: bothLendingTypes },
  { name: 'Max affordable loan', column: ProductsResultsColumn.MaxAffordableLoan, direction: 'numeric', lendingTypes: btlOnly },
  { name: 'Rental required', column: ProductsResultsColumn.RentalRequired, direction: 'numeric', lendingTypes: btlOnly },
  { name: systemDefaultOptionLabel, direction: 'default', lendingTypes: bothLendingTypes },
];

const numericSortDirectionLabels = ['Low to high', 'High to low'] as const;
const alphabeticalSortDirectionLabels = ['A - Z', 'Z - A'] as const;

export function sortDirectionLabels(direction: SortDirectionKind): readonly string[] {
  switch (direction) {
    case 'numeric':
      return numericSortDirectionLabels;
    case 'alphabetical':
      return alphabeticalSortDirectionLabels;
    default:
      return [systemDefaultOptionLabel];
  }
}

export function productSortPreferenceOptionsFor(lendingTypeCode: LendingTypeCode): ProductSortPreferenceOption[] {
  return productSortPreferenceOptions.filter(option => option.lendingTypes.includes(lendingTypeCode));
}

/**
 * Shown against anything the broker's firm has locked: beside the label on the search form, and at the top of a
 * results filter group's options panel, whose row of titles is too narrow to carry it without knocking the
 * controls out of line.
 *
 * The preferences editor's own template still writes the sentence out by hand.
 */
export const filterPreferenceLockedNote = 'Set by your firm';

/**
 * Shown in the preferences editor against a value that came from the broker's firm but is still theirs to change.
 *
 * Deliberately different wording from the lock note. "Set by your firm" is the end of the matter; this one is a
 * starting point, and the broker has to be able to tell the two apart at a glance.
 */
export const filterPreferenceInheritedNote = 'From your firm - change it to make it yours';
